/**
 * Permission Store
 *
 * Manages user permissions for access control.
 * Permissions are loaded on login and stored in localStorage.
 * Used by:
 * - Menu filtering (hide items user can't access)
 * - Route guards (redirect to forbidden if no permission)
 * - Component guards (hide/disable features)
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { logger } from '@/shared/utils/logger';
import { SecureStorage, STORAGE_KEYS } from '@/shared/utils/secure-storage';

// ============================================================================
// Constants
// ============================================================================

// ============================================================================
// Types
// ============================================================================

export interface PermissionItem {
  nodeId: number;
  functionId: string | null;
}

/**
 * Menu function item from MFUN_TBL
 */
export interface MenuFunctionItem {
  nodeId: number;
  functionId: string | null;
  functionName: string | null;
  parentId: number | null;
  sequenceNo: number;
  programId: string;
  userId: string;
  lastUpdate: string;
  path: string | null; // Route path generated from functionId
}

export interface UserPermissions {
  roleCode: string;
  permissions: PermissionItem[];
  allowedFunctions: string[];
  allowedNodeIds: number[];
  /** Full menu list for sidebar and role management */
  menuList: MenuFunctionItem[];
  /** True if user is admin (from API response) */
  isAdmin: boolean;
}

interface PermissionState {
  /** User's permissions data */
  permissions: UserPermissions | null;
  /** Whether permissions have been initialized */
  isInitialized: boolean;
  /** Whether the current user is a super admin (from API) */
  isSuperAdmin: boolean;
  /** Full menu list for navigation and permission checks */
  menuList: MenuFunctionItem[];

  /** Set permissions (called on login) */
  setPermissions: (
    roleCode: string,
    permissions: PermissionItem[],
    allowedFunctions: string[],
    allowedNodeIds: number[],
    menuList: MenuFunctionItem[],
    isAdmin: boolean,
  ) => void;

  /** Clear permissions (called on logout) */
  clearPermissions: () => void;

  /** Initialize from sessionStorage */
  initializeFromStorage: () => void;

  /** Check if user has permission for a function */
  hasPermission: (functionId: string) => boolean;

  /** Check if user has permission for a nodeId */
  hasNodePermission: (nodeId: number) => boolean;

  /** Check if user can access a route (by functionId) */
  canAccessRoute: (functionId: string) => boolean;

  /** Get all allowed function IDs */
  getAllowedFunctions: () => string[];

  /** Get menu list for sidebar */
  getMenuList: () => MenuFunctionItem[];
}

// ============================================================================
// Storage Helpers
// ============================================================================

function saveToStorage(permissions: UserPermissions): void {
  try {
    SecureStorage.setItem(
      STORAGE_KEYS.USER_PERMISSIONS,
      JSON.stringify(permissions),
    );
  } catch (error) {
    logger.warn('Failed to save permissions to storage', { error });
  }
}

function loadFromStorage(): UserPermissions | null {
  try {
    const stored = SecureStorage.getItem(STORAGE_KEYS.USER_PERMISSIONS);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as UserPermissions;
    // Validate structure
    if (parsed.roleCode && Array.isArray(parsed.allowedFunctions)) {
      return parsed;
    }
    return null;
  } catch (error) {
    logger.warn('Failed to load permissions from storage', { error });
    return null;
  }
}

function clearStorage(): void {
  try {
    SecureStorage.removeItem(STORAGE_KEYS.USER_PERMISSIONS);
  } catch (error) {
    logger.warn('Failed to clear permissions storage', { error });
  }
}

// ============================================================================
// Store
// ============================================================================

export const usePermissionStore = create<PermissionState>()(
  devtools(
    (set, get) => ({
      permissions: null,
      isInitialized: false,
      isSuperAdmin: false,
      menuList: [], // Add default menuList

      setPermissions: (
        roleCode,
        permissions,
        allowedFunctions,
        allowedNodeIds,
        menuList,
        isAdmin,
      ) => {
        const userPermissions: UserPermissions = {
          roleCode,
          permissions,
          allowedFunctions,
          allowedNodeIds,
          menuList,
          isAdmin,
        };

        // Save to sessionStorage
        saveToStorage(userPermissions);

        // Use isAdmin from API (backend determines admin status)
        const isSuperAdmin = isAdmin;

        logger.info('Permissions loaded', {
          roleCode,
          functionCount: allowedFunctions.length,
          menuCount: menuList.length,
          isSuperAdmin,
          allowedFunctions, // Log ra toàn bộ functions
          allowedNodeIds, // Log ra toàn bộ nodeIds
        });

        set(
          {
            permissions: userPermissions,
            isInitialized: true,
            isSuperAdmin,
            menuList, // Update menuList in state
          },
          false,
          'permission/setPermissions',
        );
      },

      clearPermissions: () => {
        clearStorage();
        set(
          {
            permissions: null,
            isInitialized: false,
            isSuperAdmin: false,
            menuList: [], // Clear menuList
          },
          false,
          'permission/clearPermissions',
        );
      },

      initializeFromStorage: () => {
        const stored = loadFromStorage();
        if (stored) {
          // Use isAdmin from stored data (which came from API)
          const isSuperAdmin = stored.isAdmin ?? false;
          logger.debug('Permissions restored from storage', {
            roleCode: stored.roleCode,
            functionCount: stored.allowedFunctions.length,
            isSuperAdmin,
          });
          set(
            {
              permissions: stored,
              isInitialized: true,
              isSuperAdmin,
              menuList: stored.menuList || [], // Restore menuList
            },
            false,
            'permission/initializeFromStorage',
          );
        } else {
          set(
            { isInitialized: true },
            false,
            'permission/initializeFromStorage',
          );
        }
      },

      hasPermission: (functionId: string) => {
        const { permissions } = get();

        // No permissions loaded
        if (!permissions) {
          logger.warn('hasPermission called but no permissions loaded', {
            functionId,
          });
          return false;
        }

        // Case-insensitive comparison
        const upperFunctionId = functionId.toUpperCase();
        const result = permissions.allowedFunctions.some(
          f => f.toUpperCase() === upperFunctionId,
        );

        // Debug log
        logger.debug('Permission check', {
          functionId,
          upperFunctionId,
          allowedFunctions: permissions.allowedFunctions,
          result,
        });

        return result;
      },

      hasNodePermission: (nodeId: number) => {
        const { permissions } = get();

        if (!permissions) {
          return false;
        }

        return permissions.allowedNodeIds.includes(nodeId);
      },

      canAccessRoute: (functionId: string) => {
        // Same as hasPermission but semantic naming for routes
        return get().hasPermission(functionId);
      },

      getAllowedFunctions: () => {
        const { permissions } = get();

        return permissions?.allowedFunctions ?? [];
      },

      getMenuList: () => {
        const { permissions } = get();
        return permissions?.menuList ?? [];
      },
    }),
    { name: 'permission-store' },
  ),
);
